import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getModel, removeModel, updateModel } from './requests';
import { toast } from 'react-toastify';
import { CreatePromptModelRequest, PromptModel, QueryKey } from 'api/model';
import { errorHandler } from 'api/axios_instance';

const queryKey = QueryKey.PromptModels;

type Props = {
  modelId: string;
  model?: PromptModel;
};

const useModel = ({ modelId, model: initialModel }: Props) => {
  const queryClient = useQueryClient();

  const { data: latestModel, isLoading: modelIsLoading } = useQuery<PromptModel, Error>({
    queryKey: [queryKey, { id: modelId }],
    queryFn: () => getModel(modelId),
    enabled: !!modelId && !initialModel,
  });

  const model = latestModel ?? initialModel;

  const update = useMutation({
    mutationFn: (model: CreatePromptModelRequest) => updateModel(modelId, model),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Models successfully updated');
    },
    onError: errorHandler('Updating model failed'),
  });

  const remove = useMutation({
    mutationFn: () => removeModel(modelId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Model successfully removed');
    },
    onError: errorHandler('Removing prompts model failed'),
  });

  return { model, modelIsLoading, remove, update };
};

export default useModel;
